import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Wallet, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ProjectFilter } from '@/components/ProjectFilter';
import { PageError } from '@/components/PageError';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Row { driver_id: string; code: string | null; full_name_en: string | null; balance: string; basic_salary: string | null }
interface ProjectDriver { id: string }

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DriverAccountsPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // PR MAY10 P4B.1: project filter persisted to localStorage
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectDriverIds, setProjectDriverIds] = useState<Set<string> | null>(null);

  useEffect(() => {
    apiClient.get<Row[]>('/api/v1/driver-finance/drivers/all-balances')
      .then((r) => { setRows(r.data); setError(null); })
      .catch((e) => setError(extractErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  // When a project is selected, fetch its driver list and use as a filter set.
  useEffect(() => {
    if (!projectId) { setProjectDriverIds(null); return; }
    apiClient.get<ProjectDriver[]>(`/api/v1/projects/${projectId}/drivers`)
      .then((r) => setProjectDriverIds(new Set(r.data.map(d => d.id))))
      .catch(() => setProjectDriverIds(new Set()));
  }, [projectId]);

  const filtered = useMemo(() => {
    if (!projectDriverIds) return rows;
    return rows.filter(r => projectDriverIds.has(r.driver_id));
  }, [rows, projectDriverIds]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Wallet className="h-4 w-4" /> Driver accounts</div>
          <div className="w-24" />
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-4 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <ProjectFilter value={projectId} onChange={setProjectId} storageKey="driverAccounts.projectId" />
          <span className="text-xs text-muted-foreground">
            {projectId
              ? `Showing ${filtered.length} drivers in this project`
              : `Showing ${filtered.length} drivers (all projects)`}
          </span>
        </div>
        {loading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        : error ? <PageError error={error} />
        : filtered.length === 0 ? <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">{projectId ? 'No drivers in this project.' : 'No drivers.'}</div>
        : filtered.map((r) => {
          const bal = Number(r.balance);
          return (
            <Link key={r.driver_id} to={'/dashboard/flatbed/driver-accounts/' + r.driver_id}
              className="block rounded-lg border border-border p-3 hover:bg-muted/30 transition-colors">
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-medium">{r.full_name_en ?? r.code}</div>
                <div className={'font-mono ' + (bal >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400')}>{fmtSar(bal)}</div>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{r.code} {r.basic_salary ? '· basic salary ' + fmtSar(Number(r.basic_salary)) : ''}</div>
            </Link>
          );
        })}
      </main>
    </div>
  );
}
